import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Autocomplete
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api.config';

const ProjectUsers = ({ projectId, projectName }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Teams mapping for showing user's team in table
  const [teamMembershipByUsername, setTeamMembershipByUsername] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [formData, setFormData] = useState({
    user_id: '',
    project_role_id: ''
  });
  // Edit user dialog state
  const [isEditUserDialogOpen, setIsEditUserDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    user_id: '',
    project_role_id: ''
  });
  const [editError, setEditError] = useState('');
  

  // useEffect moved below fetchData to avoid "used before defined"

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersResponse, rolesResponse, allUsersResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/project-users?project_id=${projectId}`),
        axios.get(`${API_BASE_URL}/project-roles`),
        axios.get(`${API_BASE_URL}/users`)
      ]);

      setUsers(usersResponse.data.project_users || []);
      setRoles(rolesResponse.data.project_roles || []);
      setAllUsers(allUsersResponse.data.users || []);

      // Fetch teams + members to build username -> teamName mapping
      try {
        const teamsRes = await axios.get(`${API_BASE_URL}/teams?project_id=${projectId}`);
        const teams = teamsRes.data.teams || [];

        const membership = {};
        for (const team of teams) {
          try {
            const membersRes = await axios.get(`${API_BASE_URL}/teams/${team.id}/members`);
            const members = membersRes.data.members || [];
            members.forEach(m => {
              const username = m.username || (m.user && (m.user.username || (m.user.first_name && m.user.last_name ? `${m.user.first_name} ${m.user.last_name}` : m.user.email)));
              if (!username) return;
              if (!membership[username]) {
                membership[username] = team.name || 'Team';
              }
            });
          } catch (e) {
            // ignore single team errors
          }
        }
        setTeamMembershipByUsername(membership);
      } catch (e) {
        setTeamMembershipByUsername({});
      }
      
    } catch (err) {
      console.error('Error fetching data:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      // If it's a CORS or network error, show a more helpful message
      if (err.code === 'ERR_NETWORK' || err.message.includes('CORS')) {
        setError('Unable to connect to the backend server. The new project management features require the backend to be deployed with the latest changes.');
      } else {
        const errorMessage = err.response?.data?.error || 'Error loading project users data';
        setError(`Error loading project users data: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId, fetchData]);


  const handleAddUser = async () => {
    try {
      await axios.post(`${API_BASE_URL}/project-users?project_id=${projectId}`, formData);
      setOpenDialog(false);
      setFormData({ user_id: '', project_role_id: '' });
      fetchData();
    } catch (err) {
      setError('Error adding user to project');
      console.error('Error adding user:', err);
    }
  };

  const openEditUserDialogHandler = (user) => {
    setEditUser(user);
    setEditFormData({ 
      user_id: user.user_id, 
      project_role_id: user.project_role_id 
    });
    setEditError('');
    setIsEditUserDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!editUser) return;
    try {
      await axios.put(`${API_BASE_URL}/project-users?project_id=${projectId}&user_id=${editUser.user_id}`, {
        project_role_id: editFormData.project_role_id
      });
      setIsEditUserDialogOpen(false);
      setEditUser(null);
      setEditFormData({ user_id: '', project_role_id: '' });
      fetchData();
    } catch (err) {
      console.error('Error updating user:', err);
      if (err.response?.data?.error) {
        setEditError(err.response.data.error);
      } else if (err.code === 'ERR_NETWORK' || (err.message && err.message.includes('CORS'))) {
        setEditError('Unable to connect to the backend server. Please ensure the backend is deployed with the latest changes.');
      } else {
        setEditError('Error updating user role');
      }
    }
  };

  const handleRemoveUserClick = (userId) => {
    const user = users.find(u => u.user_id === userId);
    setUserToRemove(user);
    setOpenConfirmDialog(true);
  };

  const handleConfirmRemove = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/project-users?project_id=${projectId}&user_id=${userToRemove.user_id}`);
      setOpenConfirmDialog(false);
      setUserToRemove(null);
      fetchData();
    } catch (err) {
      setError('Error removing user from project');
      console.error('Error removing user:', err);
    }
  };

  const handleCancelRemove = () => {
    setOpenConfirmDialog(false);
    setUserToRemove(null);
  };

  const getRoleColor = (roleCode) => {
    const colors = {
      'S': '#1976d2', // Service Manager - Blue
      'J': '#388e3c', // JR Service Manager - Green
      'P': '#f57c00', // People Manager - Orange
      'T': '#7b1fa2', // Team Leader - Purple
      'C': '#757575'  // Team Member - Gray
    };
    return colors[roleCode] || '#757575';
  };

  const getAvailableUsers = () => {
    const assignedUserIds = users.map(u => u.user_id);
    return allUsers.filter(user => !assignedUserIds.includes(user.id));
  };

  if (loading) {
    return <Typography>Loading project users...</Typography>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6"></Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          disabled={getAvailableUsers().length === 0}
        >
          Add User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Team</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Assigned Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.user_id}>
                <TableCell>
                  <Typography variant="body2">
                    {user.username}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {user.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.role_name}
                    size="small"
                    sx={{
                      backgroundColor: getRoleColor(user.role_code),
                      color: 'white'
                    }}
                  />
                </TableCell>
                <TableCell>
                  {teamMembershipByUsername[user.username] || '-'}
                </TableCell>
                <TableCell>
                  {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => openEditUserDialogHandler(user)}
                      sx={{ color: 'grey.500', mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveUserClick(user.user_id)}
                      sx={{ color: 'grey.500' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="textSecondary">
                    No users assigned to this project
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add User Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add User to Project</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={getAvailableUsers()}
            getOptionLabel={(option) => `${option.username} (${option.email})`}
            value={getAvailableUsers().find(user => user.id === formData.user_id) || null}
            onChange={(event, newValue) => {
              setFormData({ ...formData, user_id: newValue ? newValue.id : '' });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="User"
                required
                sx={{ mt: 1, mb: 2 }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2">{option.username}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {option.email}
                  </Typography>
                </Box>
              </Box>
            )}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.project_role_id}
              onChange={(e) => setFormData({ ...formData, project_role_id: e.target.value })}
              label="Role"
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  <Box display="flex" alignItems="center">
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: getRoleColor(role.code),
                        mr: 1
                      }}
                    />
                    {role.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleAddUser}
            variant="contained"
            disabled={!formData.user_id || !formData.project_role_id}
          >
            Add User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditUserDialogOpen} onClose={() => { setIsEditUserDialogOpen(false); setEditError(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User Role</DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError('')}>
              {editError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="User"
            value={editUser ? `${editUser.username} (${editUser.email})` : ''}
            disabled
            sx={{ mt: 1, mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={editFormData.project_role_id}
              onChange={(e) => setEditFormData({ ...editFormData, project_role_id: e.target.value })}
              label="Role"
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  <Box display="flex" alignItems="center">
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: getRoleColor(role.code),
                        mr: 1
                      }}
                    />
                    {role.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setIsEditUserDialogOpen(false); setEditError(''); }}>Cancel</Button>
          <Button
            onClick={handleUpdateUser}
            variant="contained"
            disabled={!editFormData.project_role_id}
          >
            Update Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={openConfirmDialog} onClose={handleCancelRemove} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm User Removal</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove user <strong>{userToRemove?.username}</strong> from project <strong>{projectName}</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            The user will no longer be part of the project.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemove}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRemove}
            variant="contained"
            color="error"
          >
            Remove User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectUsers; 